import React, { useMemo } from 'react';
import { useBilling } from '../../../contexts/BillingContext';
import { useData } from '../../../store/DataContext';
import { useSettingsDraft } from '../../../hooks/useSettingsDraft';
import { DirectSale } from '../../../types/billing';
import { calculateLine, documentTotals, taxFields, taxFor, todayLusaka } from '../../../utils/billingMoney';
import { DirectSaleReceiptPaper } from '../DirectSaleReceiptPaper';
import { Field, SettingsPanel, Toggle, inputCls } from './SettingsPanel';

/** Paper, margins, fonts, logo and visible fields — with a live A4 receipt preview. */
export function DocumentSettings() {
  const {
    config
  } = useBilling();
  const {
    currentUser
  } = useData();
  const s = useSettingsDraft(['appearance'], 'Printing & documents', d => {
    if (d.appearance.marginMm < 5 || d.appearance.marginMm > 30) return 'Margins must be between 5 and 30 mm.';
    if (!/^#[0-9a-f]{6}$/i.test(d.appearance.accentColor)) return 'Choose a valid colour.';
    return null;
  });
  const a = s.draft.appearance;
  const set = (patch: Partial<typeof a>) => s.update('appearance', patch);
  const sample: DirectSale = useMemo(() => {
    const tax = taxFor(config);
    const lines = [calculateLine({
      id: 'a',
      code: 'CON-GEN',
      description: 'General consultation',
      unit: 'Visit',
      quantity: 1,
      unitPrice: 250,
      discountPct: 0,
      department: 'Clinical',
      ...taxFields(tax),
      taxInclusive: config.taxes.pricesIncludeTax
    }), calculateLine({
      id: 'b',
      code: 'LAB-FBC',
      description: 'Full blood count',
      unit: 'Test',
      quantity: 1,
      unitPrice: 150,
      discountPct: 10,
      department: 'Laboratory',
      ...taxFields(tax),
      taxInclusive: config.taxes.pricesIncludeTax
    })];
    const totals = documentTotals(lines);
    return {
      id: 'sample',
      number: `${config.numbering.saleReceipt.prefix}SAMPLE`,
      state: 'Completed',
      accountName: 'Sample customer',
      accountType: 'WalkIn',
      date: todayLusaka(),
      lines,
      ...totals,
      tenders: [{
        methodId: 'cash',
        methodName: 'Cash',
        amount: totals.total
      }],
      cashTendered: Math.ceil(totals.total / 50) * 50,
      changeGiven: Math.round((Math.ceil(totals.total / 50) * 50 - totals.total) * 100) / 100,
      stockMovementIds: [],
      issuer: {
        business: config.business,
        appearance: a,
        regional: config.regional
      },
      receiptFooter: config.receipts.footer,
      cashierId: '',
      cashierName: currentUser?.fullName ?? 'Cashier',
      createdAt: new Date().toISOString()
    };
  }, [a, config, currentUser]);
  return <SettingsPanel title="Printing & documents" description="Receipts always print on A4 portrait (210 × 297 mm). Invoices, quotations, statements and reports use the paper chosen here. Issued documents keep the layout they were issued with." {...s} onSave={s.save} onReset={s.reset} data-id="element-5312" data-mp="3oiyxn67t8l47">
      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_auto]" data-id="element-5313" data-mp="39ly4cy9z0i3m">
        <div className="grid content-start gap-4 sm:grid-cols-2" data-id="element-5314" data-mp="236nrduhvp33h">
          <Field id="dc-rp" label="Receipt paper" data-id="element-5315" data-mp="2341b96gx9f9n"><input id="dc-rp" value="A4 portrait — fixed" readOnly className={`${inputCls} bg-slate-50`} data-id="element-5316" data-mp="1y71a99yxj2pe" /></Field>
          <Field id="dc-paper" label="Invoice & report paper" data-id="element-5317" data-mp="dql489igs0h8"><select id="dc-paper" value={a.paperSize} onChange={e => set({
            paperSize: e.target.value as 'A4' | 'Letter'
          })} className={inputCls} data-id="element-5318" data-mp="iocxb6uuhsn0"><option value="A4" data-id="element-5319" data-mp="nmeqwlpwdacq">A4 portrait</option><option value="Letter" data-id="element-5320" data-mp="nm4vujpzwd06">US Letter portrait</option></select></Field>
          <Field id="dc-margin" label="Margins (mm)" data-id="element-5321" data-mp="sey3pggrg1gh"><input id="dc-margin" type="number" min={5} max={30} value={a.marginMm} onChange={e => set({
            marginMm: Number(e.target.value)
          })} className={inputCls} data-id="element-5322" data-mp="xd9dxsaywdz8" /></Field>
          <Field id="dc-font" label="Font" data-id="element-5323" data-mp="11d1f3jy17huk"><select id="dc-font" value={a.font} onChange={e => set({
            font: e.target.value as typeof a.font
          })} className={inputCls} data-id="element-5324" data-mp="16dxqsjomk8aj"><option data-id="element-5325" data-mp="1bdk2goot1gh8">Open Sans</option><option data-id="element-5326" data-mp="1bbplflgnm8if">Inter</option><option data-id="element-5327" data-mp="1bc90muex6isr">Source Serif Pro</option></select></Field>
          <Field id="dc-color" label="Document colour" data-id="element-5328" data-mp="3863r5dk3pj9f"><input id="dc-color" type="color" value={a.accentColor} onChange={e => set({
            accentColor: e.target.value
          })} className="mt-1 h-10 w-full cursor-pointer rounded-md border border-slate-300 bg-white p-1" data-id="element-5329" data-mp="387eytogo3b5t" /></Field>
          <Field id="dc-logo" label="Logo position" data-id="element-5330" data-mp="12h0fm3kvbfu9"><select id="dc-logo" value={a.logoPlacement} onChange={e => set({
            logoPlacement: e.target.value as typeof a.logoPlacement
          })} className={inputCls} data-id="element-5331" data-mp="12fyv78hypgzv"><option value="left" data-id="element-5332" data-mp="1gegkhanin1jw">Left</option><option value="center" data-id="element-5333" data-mp="1cey2amn2x4kr">Centre</option><option value="right" data-id="element-5334" data-mp="1cfzewvjqtz4l">Right</option></select></Field>
          <Field id="dc-logosize" label="Logo size (mm)" data-id="element-5335" data-mp="oou1en19zixg"><input id="dc-logosize" type="number" min={10} max={40} value={a.logoSizeMm ?? 17} onChange={e => set({
            logoSizeMm: Number(e.target.value)
          })} className={inputCls} data-id="element-5336" data-mp="oniw2is6m348" /></Field>
          <div className="space-y-3 sm:col-span-2" data-id="element-5337" data-mp="xji7gfeod4nb">
            <Toggle id="dc-pn" label="Patient file number" checked={a.showPatientNumber} onChange={v => set({
            showPatientNumber: v
          })} data-id="element-5338" data-mp="sqp0dkvqhk82" />
            <Toggle id="dc-enc" label="Visit / encounter reference on invoices" checked={a.showEncounter} onChange={v => set({
            showEncounter: v
          })} data-id="element-5339" data-mp="5032unjcvicb" />
            <Toggle id="dc-staff" label="Cashier / staff name" checked={a.showStaff} onChange={v => set({
            showStaff: v
          })} data-id="element-5340" data-mp="3n1rv1tosmo6g" />
            <Toggle id="dc-disc" label="Discount column (when a line has a discount)" checked={a.showDiscountColumn} onChange={v => set({
            showDiscountColumn: v
          })} data-id="element-5341" data-mp="3rt9x6l2jyut0" />
            <Toggle id="dc-tax" label="Tax column on lines" checked={a.showTaxColumn} onChange={v => set({
            showTaxColumn: v
          })} data-id="element-5342" data-mp="3i25iznkpbdz8" />
            <Toggle id="dc-tb" label="Tax breakdown by code" checked={a.showTaxBreakdown !== false} onChange={v => set({
            showTaxBreakdown: v
          })} data-id="element-5343" data-mp="9nn87e3dlkbx" />
            <Toggle id="dc-sig" label="Signature lines" checked={a.showSignatures !== false} onChange={v => set({
            showSignatures: v
          })} data-id="element-5344" data-mp="ejc85ijb538r" />
          </div>
        </div>
        <div data-id="element-5345" data-mp="4lxufqorqsk5">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500" data-id="element-5346" data-mp="4plnypu7reyv">Live preview — sample receipt{s.dirty ? ' (unsaved changes)' : ''}</p>
          <div className="h-[560px] w-[400px] overflow-hidden rounded-lg border border-slate-200 bg-slate-100" data-id="element-5347" data-mp="2pesvnb6pv24v">
            <div className="origin-top-left scale-[0.5]" style={{
            width: '210mm'
          }} aria-hidden="true" data-id="element-5348" data-mp="vtpnjlzihcgi"><DirectSaleReceiptPaper sale={sample} data-id="element-5349" data-mp="qwz7gynpigv3" /></div>
          </div>
        </div>
      </div>
    </SettingsPanel>;
}